import type { SyncPhaseContext } from '../collab-strategy/collab-strategy.ts';

export type SyncObservation =
  | {
      kind: 'request-started';
      reason: 'connect' | 'missing-data';
      peer: string | null;
    }
  | {
      kind: 'request-ended';
      outcome: 'completed' | 'timed-out' | 'interrupted' | 'incomplete';
    }
  | { kind: 'help-started'; peer: string | null }
  | { kind: 'help-ended'; outcome: 'sent' | 'skipped' | 'failed' }
  | { kind: 'server-started'; id: number }
  | { kind: 'server-ended'; id: number; outcome: 'completed' | 'failed' };

export type SyncObserver = (event: SyncObservation) => void;

/**
 * Observer failures must never replace a transport error or interrupt a sync.
 */
function notifySyncObserver(observer: SyncObserver, event: SyncObservation): void {
  try {
    observer(event);
  } catch {
    // Diagnostics are best effort and cannot participate in protocol control flow.
  }
}

export function createSafeSyncObserver(
  observer: SyncObserver | undefined,
): SyncObserver | undefined {
  return observer ? (event) => notifySyncObserver(observer, event) : undefined;
}

/**
 * Only called when observation is enabled; the ordinary reply path is unchanged.
 */
export function observeSyncReply(
  context: SyncPhaseContext,
  peer: string | null,
  reply: (context: SyncPhaseContext) => void,
): void {
  const observer = context.onSyncObservation;
  if (!observer) {
    reply(context);
    return;
  }
  notifySyncObserver(observer, { kind: 'help-started', peer });
  let outcome: 'sent' | 'skipped' | 'failed' = 'failed';
  let sent = false;
  try {
    reply({
      ...context,
      onSyncObservation: undefined,
      send: (...args) => {
        context.send(...args);
        sent = true;
      },
      sendToPeer: (...args) => {
        context.sendToPeer(...args);
        sent = true;
      },
    });
    outcome = sent ? 'sent' : 'skipped';
  } finally {
    notifySyncObserver(observer, { kind: 'help-ended', outcome });
  }
}
